import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { policyApi } from "../../services/policyApi";
import { EagleState } from "../../components/EagleState";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { useLocalization } from "../../hooks/useLocalization";

export default function PolicyLibraryPage() {
  const { t } = useLocalization();
  const [policies, setPolicies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title_en: "", title_ja: "", category: "GENERAL", content_en: "", content_ja: "", effective_date: "" });
  const [accessDenied, setAccessDenied] = useState(false);

  const load = () => policyApi.list()
    .then((r) => { setPolicies(r.data); setAccessDenied(false); })
    .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); });
  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await policyApi.create(form);
    setForm({ title_en: "", title_ja: "", category: "GENERAL", content_en: "", content_ja: "", effective_date: "" });
    setShowForm(false);
    load();
  };

  if (accessDenied) return <AccessDenied resourceLabel={t("policies.title")} />;

  return (
    <div className="max-w-3xl" data-testid="policy-library-page">
      <PageHelpBanner pageKey="policy-library" title="How to use this page">
        <p>Publish company policies here in both English and Japanese - once published, employees must acknowledge them and their acknowledgment is tracked. Click <strong>New Policy</strong> to draft one; policies aren't visible to staff until published.</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("policies.title")}</h2>
        <button
          data-testid="new-policy-button"
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all"
        >
          <Plus size={16} /> {t("policies.newPolicy")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4 mb-6">
          <input data-testid="policy-title-en-input" required placeholder={t("policies.titleEn")} value={form.title_en} onChange={(e) => setForm((f) => ({ ...f, title_en: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="policy-title-ja-input" required placeholder={t("policies.titleJa")} value={form.title_ja} onChange={(e) => setForm((f) => ({ ...f, title_ja: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <textarea data-testid="policy-content-en-input" required placeholder={t("contracts.contentEn")} value={form.content_en} onChange={(e) => setForm((f) => ({ ...f, content_en: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" rows={3} />
          <textarea data-testid="policy-content-ja-input" required placeholder={t("contracts.contentJa")} value={form.content_ja} onChange={(e) => setForm((f) => ({ ...f, content_ja: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" rows={3} />
          <input data-testid="policy-effective-date-input" required type="date" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <button data-testid="submit-policy-button" type="submit" className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">{t("common.submit")}</button>
        </form>
      )}

      <div className="space-y-3">
        {policies.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`policy-item-${p.id}`}>
            <p className="font-semibold text-gray-800 text-sm">{p.title_en}</p>
            <p className="text-xs text-gray-500">{p.category} · {p.status}</p>
          </div>
        ))}
        {policies.length === 0 && <EagleState variant="empty" title={t("eagle.noPoliciesTitle")} message={t("eagle.noPoliciesMessage")} testId="policy-library-empty" />}
      </div>
    </div>
  );
}
